import type mapboxgl from 'mapbox-gl';
import type { GeoJSONSource, MapLayerMouseEvent } from 'mapbox-gl';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMapbox } from '@/contexts/MapboxContext';
import { formatToLocaleString } from '@/utils/date';
import { createMapPopup, setMapPopupContent } from '@/utils/map/mapPopup';
import { getShipType } from '@/utils/map/mapUtils';
import type { AreaTrafficVessel } from '../stores/useCdcStore';
import { useCdcStore } from '../stores/useCdcStore';
import { SHIP_TYPE_ICON_EXPRESSION, VESSEL_ICONS } from './CdcVesselLayer';

// Layer IDs
const AREA_TRAFFIC_VESSELS_SOURCE = 'area-traffic-vessels-source';
const AREA_TRAFFIC_VESSELS_LAYER = 'area-traffic-vessels';
const AREA_TRAFFIC_TRAIL_SOURCE = 'area-traffic-trail-source';
const AREA_TRAFFIC_TRAIL_LAYER = 'area-traffic-trail';
const AREA_TRAFFIC_AREA_SOURCE = 'area-traffic-area-source';
const AREA_TRAFFIC_LABELS_LAYER = 'area-traffic-labels';
const AREA_TRAFFIC_AREA_LAYER = 'area-traffic-area-fill';
const AREA_TRAFFIC_AREA_OUTLINE = 'area-traffic-area-outline';
const AREA_TRAFFIC_MARKERS_SOURCE = 'area-traffic-markers-source';
const AREA_TRAFFIC_MARKERS_LAYER = 'area-traffic-markers';

const SPEED_OPTIONS = [0.5, 1, 2, 4, 8];

/**
 * Check if a position is inside the bounding box
 */
function isInsideBbox(
  lat: number,
  lon: number,
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
): boolean {
  return lat >= bbox.minLat && lat <= bbox.maxLat && lon >= bbox.minLon && lon <= bbox.maxLon;
}

/**
 * Get the vessel position at a specific timestamp, but ONLY if inside the bounding box
 * Returns null if the vessel is outside the box at that time
 */
function getPositionInsideBbox(
  vessel: AreaTrafficVessel,
  targetTime: number,
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
): { lat: number; lon: number; cog?: number; heading?: number | null; sog?: number } | null {
  const positions = vessel.positions;
  if (positions.length === 0) return null;

  // Find the position closest to the target time
  let closestIdx = 0;
  let closestDiff = Math.abs(new Date(positions[0].ts).getTime() - targetTime);

  for (let i = 1; i < positions.length; i++) {
    const diff = Math.abs(new Date(positions[i].ts).getTime() - targetTime);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIdx = i;
    }
  }

  // If the closest position is too far from target time (more than 1 hour), hide the vessel
  if (closestDiff > 3600000) return null;

  const pos = positions[closestIdx];

  // Only return if INSIDE the bounding box
  if (!isInsideBbox(pos.lat, pos.lon, bbox)) {
    return null;
  }

  return pos;
}

/**
 * Get trail segments for a vessel with per-segment SOG for speed coloring
 */
interface TrailSegment {
  coordinates: [[number, number], [number, number]];
  sog: number;
}

function getTrailSegmentsInsideBbox(
  vessel: AreaTrafficVessel,
  targetTime: number,
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
): TrailSegment[] {
  const segments: TrailSegment[] = [];
  const validPositions = vessel.positions.filter(
    (pos) => new Date(pos.ts).getTime() <= targetTime && isInsideBbox(pos.lat, pos.lon, bbox),
  );

  for (let i = 1; i < validPositions.length; i++) {
    const prev = validPositions[i - 1];
    const curr = validPositions[i];
    segments.push({
      coordinates: [
        [prev.lon, prev.lat],
        [curr.lon, curr.lat],
      ],
      sog: curr.sog ?? prev.sog ?? 0,
    });
  }

  return segments;
}

/**
 * Snap a point to the nearest edge of the bounding box
 */
function snapToBboxEdge(
  lat: number,
  lon: number,
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
): { lat: number; lon: number } {
  // Clamp both coordinates inside bbox first, so the result is always on an edge
  const clampedLat = Math.max(bbox.minLat, Math.min(bbox.maxLat, lat));
  const clampedLon = Math.max(bbox.minLon, Math.min(bbox.maxLon, lon));

  const distTop = Math.abs(bbox.maxLat - clampedLat);
  const distBottom = Math.abs(clampedLat - bbox.minLat);
  const distRight = Math.abs(bbox.maxLon - clampedLon);
  const distLeft = Math.abs(clampedLon - bbox.minLon);
  const minDist = Math.min(distTop, distBottom, distRight, distLeft);

  if (minDist === distTop) return { lat: bbox.maxLat, lon: clampedLon };
  if (minDist === distBottom) return { lat: bbox.minLat, lon: clampedLon };
  if (minDist === distRight) return { lat: clampedLat, lon: bbox.maxLon };
  return { lat: clampedLat, lon: bbox.minLon };
}

/**
 * Build entry/exit marker GeoJSON features, filtered by current replay time.
 * Entry dots appear when the vessel enters; exit dots when it leaves.
 * Points are snapped to the bbox boundary edge.
 */
function buildEntryExitFeatures(
  vessels: AreaTrafficVessel[],
  bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number },
  currentTime: number,
): GeoJSON.Feature[] {
  const features: GeoJSON.Feature[] = [];

  for (const vessel of vessels) {
    if (vessel.status === 'inside') continue;

    // Find first position inside bbox (entry moment)
    if (vessel.status === 'entered' || vessel.status === 'passed_through') {
      let entryPos: { lat: number; lon: number; ts: string } | null = null;

      if (vessel.entry_point) {
        entryPos = vessel.entry_point;
      } else {
        const first = vessel.positions.find((p) => isInsideBbox(p.lat, p.lon, bbox));
        if (first) entryPos = first;
      }

      if (entryPos && new Date(entryPos.ts).getTime() <= currentTime) {
        const snapped = snapToBboxEdge(entryPos.lat, entryPos.lon, bbox);
        features.push({
          type: 'Feature',
          properties: { mmsi: vessel.mmsi, type: 'entry', name: vessel.name },
          geometry: { type: 'Point', coordinates: [snapped.lon, snapped.lat] },
        });
      }
    }

    // Find last position inside bbox (exit moment)
    if (vessel.status === 'exited' || vessel.status === 'passed_through') {
      let exitPos: { lat: number; lon: number; ts: string } | null = null;

      if (vessel.exit_point) {
        exitPos = vessel.exit_point;
      } else {
        for (let i = vessel.positions.length - 1; i >= 0; i--) {
          const p = vessel.positions[i];
          if (isInsideBbox(p.lat, p.lon, bbox)) {
            exitPos = p;
            break;
          }
        }
      }

      if (exitPos && new Date(exitPos.ts).getTime() <= currentTime) {
        const snapped = snapToBboxEdge(exitPos.lat, exitPos.lon, bbox);
        features.push({
          type: 'Feature',
          properties: { mmsi: vessel.mmsi, type: 'exit', name: vessel.name },
          geometry: { type: 'Point', coordinates: [snapped.lon, snapped.lat] },
        });
      }
    }
  }

  return features;
}

/**
 * Create GeoJSON for the selected area (blue rectangle)
 */
function createAreaGeoJSON(bbox: { minLat: number; maxLat: number; minLon: number; maxLon: number }): GeoJSON.Feature {
  return {
    type: 'Feature',
    properties: {},
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          [bbox.minLon, bbox.minLat],
          [bbox.maxLon, bbox.minLat],
          [bbox.maxLon, bbox.maxLat],
          [bbox.minLon, bbox.maxLat],
          [bbox.minLon, bbox.minLat],
        ],
      ],
    },
  };
}

/**
 * Density sparkline - pure SVG area chart for the timeline scrub bar
 */
const DensitySparkline: React.FC<{
  timeline: Array<{ ts: string; count: number }>;
  width: number;
  height: number;
}> = ({ timeline, width, height }) => {
  if (!timeline || timeline.length < 2) return null;

  const maxCount = Math.max(...timeline.map((b) => b.count), 1);
  const xStep = width / (timeline.length - 1);

  const points = timeline.map((bucket, i) => {
    const x = i * xStep;
    const y = height - (bucket.count / maxCount) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  const d = `M0,${height} L${points.join(' L')} L${width},${height} Z`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="w-full"
      preserveAspectRatio="none"
      style={{ display: 'block' }}
    >
      <title>Traffic density over time</title>
      <defs>
        <linearGradient id="area-traffic-sparkline-grad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
        </linearGradient>
      </defs>
      <path d={d} fill="url(#area-traffic-sparkline-grad)" />
      <polyline points={points.join(' ')} fill="none" stroke="#60a5fa" strokeWidth="1" strokeOpacity="0.6" />
    </svg>
  );
};

/**
 * AreaTrafficOverlay - Multi-vessel animated replay with speed-colored trails,
 * density sparkline, and entry/exit markers
 */
export const AreaTrafficOverlay: React.FC = () => {
  const { map, isMapReady } = useMapbox();
  const isActive = useCdcStore((state) => state.isAreaTrafficActive);
  const rawVessels = useCdcStore((state) => state.areaTrafficVessels);
  const bbox = useCdcStore((state) => state.areaTrafficBbox);
  const areaName = useCdcStore((state) => state.selectedAreaName);
  const stopAreaTraffic = useCdcStore((state) => state.stopAreaTraffic);
  const metadata = useCdcStore((state) => state.areaTrafficMetadata);

  // Defensive check - ensure vessels is always an array
  const vessels = Array.isArray(rawVessels) ? rawVessels : [];

  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [layersAdded, setLayersAdded] = useState(false);
  const [iconsLoaded, setIconsLoaded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);
  const popupRef = useRef<mapboxgl.Popup | null>(null);

  // Use metadata time_range from backend (real data bounds), fall back to client-side scan
  const timeRange = useCallback(() => {
    if (metadata?.time_range) {
      return {
        start: new Date(metadata.time_range.start).getTime(),
        end: new Date(metadata.time_range.end).getTime(),
      };
    }
    if (!vessels || vessels.length === 0) return { start: 0, end: 0 };
    let minTime = Number.POSITIVE_INFINITY;
    let maxTime = Number.NEGATIVE_INFINITY;
    for (const vessel of vessels) {
      for (const pos of vessel.positions) {
        const t = new Date(pos.ts).getTime();
        if (t < minTime) minTime = t;
        if (t > maxTime) maxTime = t;
      }
    }
    return { start: minTime, end: maxTime };
  }, [metadata, vessels]);

  // Clean up layers
  const cleanup = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }

    if (map.current) {
      const m = map.current;
      try {
        if (m.getLayer(AREA_TRAFFIC_LABELS_LAYER)) m.removeLayer(AREA_TRAFFIC_LABELS_LAYER);
        if (m.getLayer(AREA_TRAFFIC_VESSELS_LAYER)) m.removeLayer(AREA_TRAFFIC_VESSELS_LAYER);
        if (m.getLayer(AREA_TRAFFIC_TRAIL_LAYER)) m.removeLayer(AREA_TRAFFIC_TRAIL_LAYER);
        if (m.getLayer(AREA_TRAFFIC_MARKERS_LAYER)) m.removeLayer(AREA_TRAFFIC_MARKERS_LAYER);
        if (m.getLayer(AREA_TRAFFIC_AREA_LAYER)) m.removeLayer(AREA_TRAFFIC_AREA_LAYER);
        if (m.getLayer(AREA_TRAFFIC_AREA_OUTLINE)) m.removeLayer(AREA_TRAFFIC_AREA_OUTLINE);
        if (m.getSource(AREA_TRAFFIC_VESSELS_SOURCE)) m.removeSource(AREA_TRAFFIC_VESSELS_SOURCE);
        if (m.getSource(AREA_TRAFFIC_TRAIL_SOURCE)) m.removeSource(AREA_TRAFFIC_TRAIL_SOURCE);
        if (m.getSource(AREA_TRAFFIC_MARKERS_SOURCE)) m.removeSource(AREA_TRAFFIC_MARKERS_SOURCE);
        if (m.getSource(AREA_TRAFFIC_AREA_SOURCE)) m.removeSource(AREA_TRAFFIC_AREA_SOURCE);
      } catch {
        // Map may be destroyed
      }
    }

    setLayersAdded(false);
    setProgress(0);
    setIsPlaying(false);
  }, [map]);

  // Cleanup on unmount or when replay stops
  useEffect(() => {
    if (!isActive) {
      cleanup();
    }
    return () => cleanup();
  }, [cleanup, isActive]);

  // Load vessel icons (same as CdcVesselLayer)
  useEffect(() => {
    if (!isMapReady || !map.current || iconsLoaded) return;

    const mapInstance = map.current;
    let loadedCount = 0;

    const loadIcon = (iconId: string) => {
      if (mapInstance.hasImage(iconId)) {
        loadedCount++;
        if (loadedCount === VESSEL_ICONS.length) setIconsLoaded(true);
        return;
      }

      mapInstance.loadImage(`/vessels/${iconId}.png`, (error, image) => {
        if (!error && image && !mapInstance.hasImage(iconId)) {
          try {
            mapInstance.addImage(iconId, image);
          } catch {
            // Image may have been added by another call
          }
        }
        loadedCount++;
        if (loadedCount === VESSEL_ICONS.length) setIconsLoaded(true);
      });
    };

    VESSEL_ICONS.forEach(loadIcon);
  }, [isMapReady, map, iconsLoaded]);

  // Initialize layers when vessels are available
  useEffect(() => {
    if (!isMapReady || !map.current || !isActive || vessels.length === 0 || layersAdded || !bbox || !iconsLoaded)
      return;

    const m = map.current;

    // Add area highlight (blue rectangle)
    if (!m.getSource(AREA_TRAFFIC_AREA_SOURCE)) {
      m.addSource(AREA_TRAFFIC_AREA_SOURCE, {
        type: 'geojson',
        data: createAreaGeoJSON(bbox),
      });
    }

    if (!m.getLayer(AREA_TRAFFIC_AREA_LAYER)) {
      m.addLayer({
        id: AREA_TRAFFIC_AREA_LAYER,
        type: 'fill',
        source: AREA_TRAFFIC_AREA_SOURCE,
        paint: {
          'fill-color': '#3b82f6',
          'fill-opacity': 0.1,
        },
      });
    }

    if (!m.getLayer(AREA_TRAFFIC_AREA_OUTLINE)) {
      m.addLayer({
        id: AREA_TRAFFIC_AREA_OUTLINE,
        type: 'line',
        source: AREA_TRAFFIC_AREA_SOURCE,
        paint: {
          'line-color': '#3b82f6',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [4, 4],
        },
      });
    }

    // Entry/exit markers source (updated dynamically during animation)
    if (!m.getSource(AREA_TRAFFIC_MARKERS_SOURCE)) {
      m.addSource(AREA_TRAFFIC_MARKERS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    if (!m.getLayer(AREA_TRAFFIC_MARKERS_LAYER)) {
      m.addLayer({
        id: AREA_TRAFFIC_MARKERS_LAYER,
        type: 'circle',
        source: AREA_TRAFFIC_MARKERS_SOURCE,
        paint: {
          'circle-radius': 6,
          'circle-color': ['match', ['get', 'type'], 'entry', '#22c55e', 'exit', '#ef4444', '#9ca3af'],
          'circle-stroke-width': 2,
          'circle-stroke-color': ['match', ['get', 'type'], 'entry', '#16a34a', 'exit', '#dc2626', '#6b7280'],
          'circle-opacity': 0.9,
        },
      });
    }

    // Trail source (speed-colored segments)
    if (!m.getSource(AREA_TRAFFIC_TRAIL_SOURCE)) {
      m.addSource(AREA_TRAFFIC_TRAIL_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Trail layer with speed-interpolated color
    if (!m.getLayer(AREA_TRAFFIC_TRAIL_LAYER)) {
      m.addLayer({
        id: AREA_TRAFFIC_TRAIL_LAYER,
        type: 'line',
        source: AREA_TRAFFIC_TRAIL_SOURCE,
        paint: {
          'line-color': [
            'interpolate',
            ['linear'],
            ['coalesce', ['get', 'sog'], 0],
            0,
            '#3b82f6', // 0 kn = blue (stationary)
            3,
            '#60a5fa', // 3 kn = light blue
            8,
            '#facc15', // 8 kn = yellow (moderate)
            15,
            '#f97316', // 15 kn = orange (fast)
            25,
            '#ef4444', // 25+ kn = red (very fast)
          ],
          'line-width': 2,
          'line-opacity': 0.5,
        },
      });
    }

    // Vessels source (updated during animation)
    if (!m.getSource(AREA_TRAFFIC_VESSELS_SOURCE)) {
      m.addSource(AREA_TRAFFIC_VESSELS_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }

    // Vessels layer using same icon config as CDC layer, with heading-based rotation
    if (!m.getLayer(AREA_TRAFFIC_VESSELS_LAYER)) {
      m.addLayer({
        id: AREA_TRAFFIC_VESSELS_LAYER,
        type: 'symbol',
        source: AREA_TRAFFIC_VESSELS_SOURCE,
        layout: {
          'icon-image': SHIP_TYPE_ICON_EXPRESSION,
          'icon-size': 0.1,
          'icon-rotate': [
            'case',
            ['all', ['has', 'heading'], ['!=', ['get', 'heading'], null]],
            ['get', 'heading'],
            ['has', 'cog'],
            ['get', 'cog'],
            0,
          ],
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
        },
      });
    }

    // Vessel name labels beneath icons (same style as CdcVesselLayer)
    if (!m.getLayer(AREA_TRAFFIC_LABELS_LAYER)) {
      m.addLayer({
        id: AREA_TRAFFIC_LABELS_LAYER,
        type: 'symbol',
        source: AREA_TRAFFIC_VESSELS_SOURCE,
        layout: {
          'text-field': ['get', 'name'],
          'text-size': 11,
          'text-offset': [0, 1.5],
          'text-anchor': 'top',
          'text-optional': true,
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 1,
        },
      });
    }

    // Fit map to area bounds with padding
    m.fitBounds(
      [
        [bbox.minLon, bbox.minLat],
        [bbox.maxLon, bbox.maxLat],
      ],
      { padding: 80 },
    );

    setLayersAdded(true);
  }, [isMapReady, map, isActive, vessels, layersAdded, bbox, iconsLoaded]);

  // Animation loop - 240 seconds at 1x speed (4 minutes)
  useEffect(() => {
    if (!isPlaying || !layersAdded || vessels.length === 0) return;

    const animate = (timestamp: number) => {
      if (!lastTimeRef.current) lastTimeRef.current = timestamp;
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      const duration = 240000 / playbackSpeed;
      const increment = (delta / duration) * 100;

      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          setIsPlaying(false);
          return 100;
        }
        return next;
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    lastTimeRef.current = 0;
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isPlaying, layersAdded, vessels.length, playbackSpeed]);

  // Update vessel positions and speed-colored trail segments based on progress
  useEffect(() => {
    if (!map.current || !layersAdded || vessels.length === 0 || !bbox) return;

    const m = map.current;
    const { start, end } = timeRange();
    const currentTime = start + (progress / 100) * (end - start);

    const vesselFeatures: GeoJSON.Feature[] = [];
    const trailFeatures: GeoJSON.Feature[] = [];

    for (const vessel of vessels) {
      const pos = getPositionInsideBbox(vessel, currentTime, bbox);
      if (pos) {
        vesselFeatures.push({
          type: 'Feature',
          properties: {
            mmsi: vessel.mmsi,
            ship_type: vessel.ship_type,
            cog: pos.cog || 0,
            heading: pos.heading ?? null,
            sog: pos.sog ?? 0,
            name: vessel.name,
          },
          geometry: {
            type: 'Point',
            coordinates: [pos.lon, pos.lat],
          },
        });
      }

      // Speed-colored trail segments (each segment carries its own sog)
      const segments = getTrailSegmentsInsideBbox(vessel, currentTime, bbox);
      for (const seg of segments) {
        trailFeatures.push({
          type: 'Feature',
          properties: { mmsi: vessel.mmsi, sog: seg.sog },
          geometry: { type: 'LineString', coordinates: seg.coordinates },
        });
      }
    }

    const vesselSource = m.getSource(AREA_TRAFFIC_VESSELS_SOURCE) as GeoJSONSource | undefined;
    if (vesselSource) {
      vesselSource.setData({ type: 'FeatureCollection', features: vesselFeatures });
    }

    const trailSource = m.getSource(AREA_TRAFFIC_TRAIL_SOURCE) as GeoJSONSource | undefined;
    if (trailSource) {
      trailSource.setData({ type: 'FeatureCollection', features: trailFeatures });
    }

    // Update entry/exit markers (time-filtered, snapped to bbox edge)
    const markerFeatures = buildEntryExitFeatures(vessels, bbox, currentTime);
    const markerSource = m.getSource(AREA_TRAFFIC_MARKERS_SOURCE) as GeoJSONSource | undefined;
    if (markerSource) {
      markerSource.setData({ type: 'FeatureCollection', features: markerFeatures });
    }
  }, [progress, map, layersAdded, vessels, timeRange, bbox]);

  // Handle hover popup when paused
  useEffect(() => {
    if (!isMapReady || !map.current || !layersAdded || !bbox) return;

    const mapInstance = map.current;

    const handleMouseEnter = (e: MapLayerMouseEvent) => {
      if (isPlaying) return;

      mapInstance.getCanvas().style.cursor = 'pointer';

      if (e.features && e.features.length > 0) {
        const feature = e.features[0];
        const props = feature.properties || {};
        const mmsi = props.mmsi as string;
        const shipType = props.ship_type as number | undefined;
        const cog = props.cog as number | undefined;
        const heading = props.heading as number | null | undefined;
        const name = props.name as string | undefined;
        const geometry = feature.geometry as GeoJSON.Point;
        const coordinates = geometry?.coordinates as [number, number];

        if (!coordinates || !mmsi) return;

        const vessel = vessels.find((v) => v.mmsi === mmsi);
        const { start, end } = timeRange();
        const currentTime = start + (progress / 100) * (end - start);

        const popupProps = {
          badge: { label: getShipType(shipType), variant: 'info' as const },
          title: name || vessel?.name || `MMSI ${mmsi}`,
          subtitle: `Replay time: ${formatToLocaleString(new Date(currentTime))}`,
          sections: [
            {
              id: 'identity',
              columns: 2,
              fields: [
                { label: 'MMSI', value: mmsi, status: 'default' as const },
                {
                  label: 'IMO',
                  value: vessel?.imo || 'Unknown',
                  status: vessel?.imo ? ('default' as const) : ('warning' as const),
                },
                {
                  label: 'Flag',
                  value: vessel?.flag_country || 'Unknown',
                  status: vessel?.flag_country ? ('default' as const) : ('warning' as const),
                },
                {
                  label: 'Heading',
                  value: heading != null ? `${heading.toFixed(0)}°` : cog != null ? `${cog.toFixed(0)}° (COG)` : 'N/A',
                },
              ],
            },
            {
              id: 'location',
              columns: 2,
              fields: [
                {
                  label: 'Position',
                  value: `${coordinates[1].toFixed(4)}, ${coordinates[0].toFixed(4)}`,
                  valueClassName: 'font-mono',
                },
                { label: 'Status', value: vessel?.status || 'In area' },
                { label: 'Destination', value: vessel?.destination || 'Unknown' },
                {
                  label: 'Speed',
                  value: props.sog != null ? `${Number(props.sog).toFixed(1)} kn` : 'N/A',
                },
              ],
            },
          ],
        };

        if (!popupRef.current) {
          popupRef.current = createMapPopup(coordinates, popupProps);
        } else {
          popupRef.current.remove();
          setMapPopupContent(popupRef.current, coordinates, popupProps);
        }
        popupRef.current.addTo(mapInstance);
      }
    };

    const handleMouseLeave = () => {
      mapInstance.getCanvas().style.cursor = '';
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };

    mapInstance.on('mouseenter', AREA_TRAFFIC_VESSELS_LAYER, handleMouseEnter);
    mapInstance.on('mouseleave', AREA_TRAFFIC_VESSELS_LAYER, handleMouseLeave);

    return () => {
      mapInstance.off('mouseenter', AREA_TRAFFIC_VESSELS_LAYER, handleMouseEnter);
      mapInstance.off('mouseleave', AREA_TRAFFIC_VESSELS_LAYER, handleMouseLeave);
    };
  }, [isMapReady, map, layersAdded, isPlaying, vessels, progress, timeRange, bbox]);

  // Don't render if not active
  if (!isActive || vessels.length === 0) return null;

  const handleClose = () => {
    cleanup();
    stopAreaTraffic();
  };

  const handlePlayPause = () => {
    if (progress >= 100) setProgress(0);
    setIsPlaying(!isPlaying);
  };

  const handleSpeedChange = () => {
    const currentIdx = SPEED_OPTIONS.indexOf(playbackSpeed);
    const nextIdx = (currentIdx + 1) % SPEED_OPTIONS.length;
    setPlaybackSpeed(SPEED_OPTIONS[nextIdx]);
  };

  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProgress(Number(e.target.value));
    setIsPlaying(false);
  };

  const { start, end } = timeRange();
  const currentTime = start + (progress / 100) * (end - start);
  const currentTimeStr = formatToLocaleString(new Date(currentTime));
  const startTimeStr = formatToLocaleString(new Date(start), { month: 'short', day: '2-digit' });
  const endTimeStr = formatToLocaleString(new Date(end), { month: 'short', day: '2-digit' });

  // Count how many vessels are currently visible (inside bbox at current time)
  const visibleCount = vessels.filter((v) => bbox && getPositionInsideBbox(v, currentTime, bbox)).length;

  if (isMinimized) {
    return (
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 pointer-events-auto">
        <div className="bg-panel-surface backdrop-blur-xl border border-panel-border rounded-2xl shadow-2xl overflow-hidden min-w-[340px]">
          {/* Compact header with name, count, and controls */}
          <div className="flex items-center gap-3 px-3 py-2">
            <span className="text-white font-medium text-sm truncate">{areaName || 'Area Traffic'}</span>
            <span className="text-gray-500 text-xs whitespace-nowrap">
              {visibleCount}/{vessels.length}
            </span>

            <div className="flex items-center gap-1 ml-auto">
              {/* Skip back */}
              <button
                type="button"
                onClick={() => setProgress(Math.max(0, progress - 5))}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <title>Skip back</title>
                  <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
                </svg>
              </button>

              {/* Play/Pause */}
              <button
                type="button"
                onClick={handlePlayPause}
                className="w-8 h-8 rounded-full bg-white text-gray-900 flex items-center justify-center shadow hover:bg-gray-100"
              >
                {isPlaying ? (
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <title>Pause</title>
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-3.5 h-3.5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                    <title>Play</title>
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              {/* Skip forward */}
              <button
                type="button"
                onClick={() => setProgress(Math.min(100, progress + 5))}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <title>Skip forward</title>
                  <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
                </svg>
              </button>

              {/* Speed */}
              <button
                type="button"
                onClick={handleSpeedChange}
                className="px-2 py-1 rounded-md bg-white/10 hover:bg-white/20 text-xs text-white font-medium transition-colors"
              >
                {playbackSpeed}x
              </button>

              {/* Expand */}
              <button
                type="button"
                onClick={() => setIsMinimized(false)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <title>Expand</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>

              {/* Close */}
              <button
                type="button"
                onClick={handleClose}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <title>Close</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Sparkline + scrub bar */}
          <div className="px-3 pb-2">
            {metadata?.timeline && metadata.timeline.length > 1 && (
              <div className="mb-1">
                <DensitySparkline timeline={metadata.timeline} width={320} height={20} />
              </div>
            )}
            <input
              type="range"
              min="0"
              max="100"
              value={progress}
              onChange={handleProgressChange}
              className="w-full h-1.5 bg-white/20 rounded-full appearance-none cursor-pointer
                [&::-webkit-slider-thumb]:appearance-none
                [&::-webkit-slider-thumb]:w-3
                [&::-webkit-slider-thumb]:h-3
                [&::-webkit-slider-thumb]:rounded-full
                [&::-webkit-slider-thumb]:bg-white
                [&::-webkit-slider-thumb]:shadow
                [&::-webkit-slider-thumb]:cursor-pointer"
            />
            <div className="text-center text-gray-400 text-xs mt-1">{currentTimeStr}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-50 pointer-events-auto">
      <div className="bg-panel-surface backdrop-blur-xl border border-panel-border rounded-2xl shadow-2xl overflow-hidden min-w-[400px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-panel-border">
          <div className="flex items-center gap-3">
            <span className="text-white font-medium text-sm">{areaName || 'Area Traffic'}</span>
            <span className="text-gray-400 text-sm">
              {visibleCount} / {vessels.length} visible
            </span>
          </div>
          <div className="flex items-center gap-1">
            {/* Minimize */}
            <button
              type="button"
              onClick={() => setIsMinimized(true)}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <title>Minimize</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {/* Close */}
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <title>Close</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Timeline */}
        <div className="px-4 py-4">
          <div className="text-center text-white text-lg font-medium mb-3">{currentTimeStr}</div>
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
            onChange={handleProgressChange}
            className="w-full h-2 bg-white/20 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-4
              [&::-webkit-slider-thumb]:h-4
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-white
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:cursor-pointer"
          />
          {/* Density sparkline with label */}
          {metadata?.timeline && metadata.timeline.length > 1 && (
            <div className="mt-1 -mb-1 px-0.5 relative">
              <DensitySparkline timeline={metadata.timeline} width={400} height={24} />
              <span className="absolute right-1 top-0 text-[9px] text-gray-500 leading-none">vessel count</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span>{startTimeStr}</span>
            <span>{endTimeStr}</span>
          </div>
        </div>

        {/* Playback controls */}
        <div className="flex items-center justify-center gap-6 px-4 py-4 border-t border-panel-border">
          {/* Skip back */}
          <button
            type="button"
            onClick={() => setProgress(Math.max(0, progress - 5))}
            className="text-gray-400 hover:text-white transition-colors p-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <title>Skip back</title>
              <path d="M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z" />
            </svg>
          </button>

          {/* Play/Pause */}
          <button
            type="button"
            onClick={handlePlayPause}
            className="w-12 h-12 rounded-full bg-white text-gray-900 flex items-center justify-center transition-colors shadow-lg hover:bg-gray-100"
          >
            {isPlaying ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <title>Pause</title>
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <title>Play</title>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          {/* Skip forward */}
          <button
            type="button"
            onClick={() => setProgress(Math.min(100, progress + 5))}
            className="text-gray-400 hover:text-white transition-colors p-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <title>Skip forward</title>
              <path d="M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z" />
            </svg>
          </button>

          {/* Speed */}
          <button
            type="button"
            onClick={handleSpeedChange}
            className="px-3 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm text-white font-medium transition-colors min-w-[48px]"
          >
            {playbackSpeed}x
          </button>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-panel-border gap-4">
          {/* Trail speed legend */}
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-500">Trail speed</span>
            <div className="flex items-center gap-0.5">
              <span className="text-[9px] text-gray-500">Slow</span>
              <div
                className="w-16 h-1.5 rounded-full"
                style={{
                  background: 'linear-gradient(to right, #3b82f6, #60a5fa, #facc15, #f97316, #ef4444)',
                }}
              />
              <span className="text-[9px] text-gray-500">Fast</span>
            </div>
          </div>

          {/* Entry/exit legend */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-green-500 border border-green-600" />
              <span className="text-[10px] text-gray-500">Entry</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500 border border-red-600" />
              <span className="text-[10px] text-gray-500">Exit</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
